import type {
  JoinCommunityResponse,
  LeaveCommunityResponse,
} from '@wix/ambassador-members-v1-member/types';
import type { UnblockMemberResponse } from '@wix/ambassador-members-v1-member-to-member-block/types';
import { withResolvedError } from '@wix/members-area-commons-ts';
import type { FlowAPI } from '@wix/yoshi-flow-editor';

import type {
  GetAccountDataOptions,
  MyAccountMiddlewareService as IMyAccountMiddlewareService,
  UpdateAccountDataOptions,
  UpdateAccountDataResponse,
} from '../types';
import type { AccountData } from '../types/server';

type ErrorHandler = FlowAPI['errorHandler'];
type HttpClient = FlowAPI['httpClient'];

type MyAccountMiddlewareServiceOptions = {
  httpClient: HttpClient;
  errorHandler: ErrorHandler;
};

export class MyAccountMiddlewareService implements IMyAccountMiddlewareService {
  private readonly httpClient: HttpClient;
  private readonly errorHandler: ErrorHandler;

  constructor({ httpClient, errorHandler }: MyAccountMiddlewareServiceOptions) {
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
  }
  async getAccountData(options: GetAccountDataOptions) {
    const requestFn = () =>
      this.httpClient.get<AccountData>(
        '/_serverless/my-account-middleware/account-data',
        {
          params: options,
        },
      );

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async isSlugAvailable(slug: string) {
    const requestFn = () =>
      this.httpClient.get<boolean>(
        '/_serverless/my-account-middleware/slug/is-available',
        {
          params: { slug },
        },
      );

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async updateAccountData(options: UpdateAccountDataOptions) {
    const requestFn = () =>
      this.httpClient.post<UpdateAccountDataResponse>(
        '/_serverless/my-account-middleware/account-data/update',
        options,
      );

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async updateSlug(slug: string) {
    const requestFn = () =>
      this.httpClient.post<string>(
        '/_serverless/my-account-middleware/slug/update',
        { slug },
      );

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async joinCommunity() {
    const requestFn = () =>
      this.httpClient.post<JoinCommunityResponse>(
        '/_serverless/my-account-middleware/join-community',
      );

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async leaveCommunity() {
    const requestFn = () =>
      this.httpClient.post<LeaveCommunityResponse>(
        '/_serverless/my-account-middleware/leave-community',
      );

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }

  async unblockMember(memberId: string) {
    const requestFn = () =>
      this.httpClient.post<UnblockMemberResponse>(
        '/_serverless/my-account-middleware/unblock-member',
        { memberId },
      );

    const { data } = await withResolvedError(requestFn, this.errorHandler, {
      errorCodesMap: {},
    });

    return data;
  }
}
